import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, Shield, Trash2, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ItemQualitySummary } from "@/components/ItemQualitySummary";
import { cn } from "@/lib/utils";
import { readingLevelToGrade, type Question } from "@shared/schema";
import type { SocratesQuestionData } from "@/components/QuestionCard";
import { apiClient } from "@/lib/apiClient";
import { useToast } from "@/hooks/use-toast";
import { sortQuestionAnswers, remapFeedbackLetters } from "@/utils/sortAnswers";
import {
  DONT_TEACH_KEY,
  DONT_TEACH_STEM_GUIDANCE,
  DONT_TEACH_OPTIONS_GUIDANCE,
  dontTeachGuidance,
  dontTeachOptionsMessage,
  dontTeachAdvisorySuggestion,
} from "@/utils/socratesDontTeach";
import {
  effectiveCycle,
  feedbackForCycle,
  isInternalPipelineMarker,
  isScored,
} from "@/utils/socratesScore";
import {
  bucketSocratesFindings,
} from "@/utils/socratesFeedback";
import {
  CRITERIA_LABELS,
  NOT_EVALUATED,
  judgmentCriterionKeys,
  notEvaluatedReason,
} from "@/utils/socratesCriteria";

interface QuestionEditModalProps {
  question: Question;
  initialSocratesData?: SocratesQuestionData;
  /** Topic name for the question — used as context when the modal
   *  asks Blacksmith for a new distractor via the regenerate-option
   *  endpoint. Defaults to "General" when not provided. */
  topicName?: string;
  /** The parent test's reading level — converted to a numeric grade and
   *  sent on the regenerate call so Socrates evaluates the item at the
   *  user's chosen grade, not a hardcoded default. */
  readingLevel?: string;
  /** Other items on the test, for the server's cross-item duplication
   *  check on the add-option regenerate call. Excludes the item being
   *  edited — see ARC-111. */
  existingItems?: Array<{ stem: string; answers: string[] }>;
  onSave: (updatedQuestion: Question, score: SocratesQuestionData) => void;
  onClose: () => void;
}

type ScoringState = "idle" | "scoring" | "complete" | "error";

export function QuestionEditModal({ question, initialSocratesData, topicName, readingLevel, existingItems, onSave, onClose }: QuestionEditModalProps) {
  const { toast } = useToast();
  const [stem, setStem] = useState(question.stem);
  // Pre-sort on open so the teacher scores options in their final saved
  // order — prevents an ordering penalty that would disappear on Update.
  // Lazy initializer runs once at mount, not on every render.
  const [initialSorted] = useState(() => sortQuestionAnswers({ ...question }));
  const answersReordered =
    initialSorted.answers.some((a, i) => a !== question.answers[i]) ||
    initialSorted.correctAnswer !== question.correctAnswer;
  const [answers, setAnswers] = useState<string[]>([...initialSorted.answers]);
  const [correctAnswer, setCorrectAnswer] = useState(initialSorted.correctAnswer);
  // If sorting changed the order, discard the stored score — it was computed
  // against the unsorted options and carries an ordering penalty that the sort
  // will eliminate. Require a fresh score. Otherwise keep the stored score
  // so Update is immediately available for questions that were already sorted.
  const [scoringState, setScoringState] = useState<ScoringState>(
    initialSocratesData && !answersReordered ? "complete" : "idle",
  );
  const [scoreData, setScoreData] = useState<SocratesQuestionData | undefined>(
    answersReordered ? undefined : initialSocratesData,
  );
  const [scoreError, setScoreError] = useState<string | undefined>();
  // Set when the user clicks the (visually disabled) Update button before
  // scoring — flashes a red border on the Score button to point them at the
  // step they're missing. Cleared once they actually score.
  const [highlightScore, setHighlightScore] = useState(false);
  // Index of the row currently being filled by an async
  // generate-distractor call (null when idle). Only one at a time —
  // the API takes the current options as context, so two concurrent
  // calls would step on each other's results.
  const [generatingAt, setGeneratingAt] = useState<number | null>(null);
  // Server-side cross-item duplication warning from the last add-option
  // regenerate call, folded into the saved question so the card banner
  // survives the modal. Reset on content edits like the score.
  const [duplicateWarning, setDuplicateWarning] = useState<Question["duplicate_warning"]>(undefined);

  const handleScore = async () => {
    setHighlightScore(false);
    setScoringState("scoring");
    setScoreError(undefined);
    try {
      const response = await apiClient("/api/questions/score", {
        method: "POST",
        body: JSON.stringify({ stem, answers, correctAnswer, targetGrade: readingLevelToGrade(readingLevel) }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setScoreData(data);
      setScoringState("complete");
    } catch (err) {
      setScoreError(err instanceof Error ? err.message : "Scoring failed");
      setScoringState("error");
    }
  };

  const handleSave = () => {
    if (!scoreData) return;
    // Sort options at save time so the user gets a stable editing
    // experience while typing (no mid-keystroke shuffling). Capitalization
    // is NOT normalized here — manual text stays as the user wrote it,
    // matching the convention everywhere else for user-edited answers.
    // correctAnswer is re-mapped by sortQuestionAnswers itself.
    const sorted = sortQuestionAnswers({ ...question, stem, answers, correctAnswer, duplicate_warning: duplicateWarning });
    // sortQuestionAnswers strips any incoming answerLetterMap: an identity
    // sort must not carry a stale map into a fresh in-modal score, which
    // refers to the displayed order already. The one exception is a save
    // with the inherited session score (no re-score, answers untouched):
    // that score still refers to the pre-sort emission order and needs its
    // map back.
    if (scoreData === initialSocratesData && question.answerLetterMap) {
      sorted.answerLetterMap = question.answerLetterMap;
    }
    onSave(sorted, scoreData);
  };

  // Reset score state when content changes so user must re-score
  const handleStemChange = (v: string) => {
    setStem(v);
    setDuplicateWarning(undefined);
    if (scoringState === "complete") { setScoringState("idle"); setScoreData(undefined); }
  };
  const handleAnswerChange = (i: number, v: string) => {
    const next = [...answers];
    next[i] = v;
    setAnswers(next);
    setDuplicateWarning(undefined);
    if (scoringState === "complete") { setScoringState("idle"); setScoreData(undefined); }
  };
  const handleCorrectAnswerChange = (v: string) => {
    setCorrectAnswer(Number(v));
    setDuplicateWarning(undefined);
    if (scoringState === "complete") { setScoringState("idle"); setScoreData(undefined); }
  };

  // Remove one answer slot. The UI hides the button at the schema minimum
  // (2 options). When the deleted slot was the correct answer, default to
  // option A — the user can re-pick before scoring.
  const handleDeleteAnswer = (i: number) => {
    if (answers.length <= 2) return;
    const next = answers.filter((_, idx) => idx !== i);
    let nextCorrect = correctAnswer;
    if (i < nextCorrect) nextCorrect -= 1;
    else if (i === nextCorrect) nextCorrect = 0;
    setAnswers(next);
    setCorrectAnswer(nextCorrect);
    setDuplicateWarning(undefined);
    if (scoringState === "complete") { setScoringState("idle"); setScoreData(undefined); }
  };

  // Append a new slot AND fill it via the same generate-distractor
  // path the card used to call. The slot appears immediately with a
  // spinner; once Blacksmith returns, we splice in the first option
  // that isn't the returned correct answer and isn't already in our
  // list. Any add forces a re-score before Update is enabled. Cap at 8.
  const handleAddAnswer = async () => {
    if (answers.length >= 6) return;
    if (generatingAt !== null) return;
    const newIndex = answers.length;
    const baseAnswers = [...answers];

    setAnswers([...baseAnswers, ""]);
    setGeneratingAt(newIndex);
    setScoringState("idle");
    setScoreData(undefined);

    try {
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem,
            answers: baseAnswers,
            correctAnswer,
            level: question.level,
            difficulty: question.difficulty,
          },
          topicName: topicName ?? "General",
          grade: readingLevelToGrade(readingLevel),
          standards: question.standards,
          regenerationTarget: "distractor",
          // Other items on the test for the server's cross-item duplicate
          // check. Excludes the item getting the new option.
          existingItems,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDuplicateWarning(data.duplicate_warning);
      // One toast regardless of how many reasons the flag carries.
      if (data.duplicate_warning?.flagged) {
        toast({ title: "Possible duplicate", description: "This item may be similar to an existing item." });
      }
      // Prefer an option that's NOT the returned correct answer AND
      // isn't already one of ours; fall back to any non-correct option;
      // last-ditch empty string keeps the slot but stays user-editable.
      const candidate: string =
        (Array.isArray(data.answers)
          ? data.answers.find(
              (a: string, i: number) => i !== data.correctAnswer && !baseAnswers.includes(a),
            )
          : undefined) ??
        (Array.isArray(data.answers)
          ? data.answers.find((_: string, i: number) => i !== data.correctAnswer)
          : undefined) ??
        "";
      setAnswers((prev) => {
        const next = [...prev];
        next[newIndex] = candidate;
        return next;
      });
    } catch (err) {
      console.error("Failed to generate new option:", err);
      // Revert the placeholder slot so the user isn't stuck with an
      // empty row from a failed call.
      setAnswers((prev) => prev.filter((_, i) => i !== newIndex));
    } finally {
      setGeneratingAt(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Question</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stem */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-stem">Question stem</Label>
            <Textarea
              id="edit-stem"
              value={stem}
              onChange={(e) => handleStemChange(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          {/* Answers */}
          <div className="space-y-1.5">
            <Label>Answer choices — select the correct answer</Label>
            <RadioGroup value={String(correctAnswer)} onValueChange={handleCorrectAnswerChange}>
              {answers.map((answer, i) => {
                const isGeneratingHere = generatingAt === i;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <RadioGroupItem
                      value={String(i)}
                      id={`edit-answer-${i}`}
                      disabled={isGeneratingHere}
                    />
                    <Label htmlFor={`edit-answer-${i}`} className="text-xs font-mono w-4 shrink-0">
                      {String.fromCharCode(65 + i)}
                    </Label>
                    {isGeneratingHere ? (
                      <div
                        className="flex-1 flex items-center gap-2 text-sm text-muted-foreground italic"
                        aria-live="polite"
                      >
                        <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                        <span>Generating option…</span>
                      </div>
                    ) : (
                      <Input
                        value={answer}
                        onChange={(e) => handleAnswerChange(i, e.target.value)}
                        className="flex-1"
                        placeholder={`Option ${String.fromCharCode(65 + i)}`}
                      />
                    )}
                    {i === correctAnswer ? (
                      <span className="text-xs text-green-600 font-medium w-14 shrink-0 text-right">✓ Correct</span>
                    ) : (
                      <span className="w-14 shrink-0" />
                    )}
                    {answers.length > 2 && (
                      i === correctAnswer ? (
                        // Deleting the correct answer would silently demote
                        // option A to "correct" and leave the question in an
                        // invalid state until the user re-picks. Warn first.
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive shrink-0"
                              disabled={generatingAt !== null}
                              title="Delete the correct answer"
                              aria-label={`Delete option ${String.fromCharCode(65 + i)}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete the correct answer?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Option {String.fromCharCode(65 + i)} is currently marked correct.
                                Deleting it will default the correct answer to option A.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteAnswer(i)}>
                                Delete anyway
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive shrink-0"
                          onClick={() => handleDeleteAnswer(i)}
                          disabled={generatingAt !== null}
                          title="Delete this option"
                          aria-label={`Delete option ${String.fromCharCode(65 + i)}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )
                    )}
                  </div>
                );
              })}
            </RadioGroup>
            {answers.length < 6 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddAnswer}
                disabled={generatingAt !== null}
                // Label stays "+ Add option" the whole time. While a
                // generation is in flight the button just fades and goes
                // unclickable; the row's own spinner already conveys
                // what's happening.
                className={cn(
                  "text-xs text-muted-foreground -ml-1",
                  generatingAt !== null && "opacity-50",
                )}
              >
                Add option <Plus className="w-3 h-3" />
              </Button>
            )}
          </div>

                    {/* Score result. Container + header color tracks the same
              green / orange / red thresholds the card surface uses, so a
              failing modal reads as obviously failing without re-checking
              the count. */}
          {scoreData && scoringState === "complete" && (() => {
            // Same ARC-39 rules as the item card: a cde_duplicate /
            // parse_failure row renders its scored sibling; zero criteria out
            // of zero evaluated is never a passing grade.
            const cycle = effectiveCycle(scoreData.cycles);
            if (!isScored(cycle)) {
              return (
                <div className="rounded-md border p-3 space-y-2 border-amber-300 bg-amber-50">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-700">
                      Item Quality: not analysed
                    </span>
                  </div>
                  <p className="text-xs text-amber-700">
                    This item hasn't been scored by the quality pipeline, so
                    there are no criteria to display.
                  </p>
                </div>
              );
            }
            const lastCycle = cycle!;
            // Live Blacksmith payload can omit error_flags/issues — default them.
            const flags = lastCycle.error_flags ?? {};
            const rawIssues = lastCycle.issues ?? [];
            // A fresh in-modal score is taken against the answers in the
            // displayed (sorted) order, so its letters already match the rows;
            // only the inherited card score references BCE's pre-sort order and
            // needs the answerLetterMap remap.
            const scoreIsInherited = scoreData === initialSocratesData;
            const letterMap = scoreIsInherited ? question.answerLetterMap : undefined;
            // Internal pipeline markers (no rule-family prefix, e.g.
            // cde_duplicate_*, parse_failure) must never reach the teacher.
            const issues = rawIssues
              .filter((raw) => !isInternalPipelineMarker(raw))
              .map((s) => remapFeedbackLetters(s, letterMap));
            const failedCount = Object.values(flags).filter(v => v === 1).length;
            const notEvaluated = Object.values(flags).filter(v => v === -1).length;
            const totalCriteria = Object.keys(flags).length;
            const evaluatedCount = totalCriteria - notEvaluated;
            const passedCount = Object.values(flags).filter(v => v === 0).length;
            const failedEntries = Object.entries(flags).filter(([, v]) => v === 1);
            const notEvaluatedEntries = Object.entries(flags).filter(([, v]) => v === -1);
            // ARC-223: a criterion is in the judgment state when it is flagged
            // AND its advisory entry carries severity=advisory — or it is
            // Don't Teach, the temporary hardcoded fallback (see
            // judgmentCriterionKeys). The summary line splits the failed
            // bucket: judgment criteria render under "needs your judgment",
            // never under "failed". The tone below keeps counting every
            // flagged criterion exactly as before — presentation only.
            const scoreFeedback = feedbackForCycle(lastCycle, scoreData?.quality_feedback);
            const judgmentKeys = judgmentCriterionKeys(flags, scoreFeedback);
            // ARC-354: findings are bucketed by their OWN severity — advisory
            // entries are judgment calls (blue), every other finding is a
            // failure (red). A criterion can carry one advisory AND one failed
            // entry and then appears under both headings; the tally counts it
            // under both. A fresh in-modal score is taken against the
            // displayed options, so letters already match — letterMap is
            // undefined, making the remap a no-op (scoreIsInherited handles
            // the same distinction the card uses for its session score).
            const remapForModal = (s: string): string => remapFeedbackLetters(s, letterMap);
            const {
              failed: failedIssuesByCriterion,
              judgment: judgmentIssuesByCriterion,
              unattributed: unattributedFeedback,
            } = bucketSocratesFindings({
              flags,
              feedback: scoreFeedback,
              rawIssues,
              remappedIssues: issues,
              remap: remapForModal,
              judgmentKeys,
            });
            // Same per-heading rule as the card: an "Open issues" entry is a
            // criterion with ≥1 failed finding; a "Needs your judgment" entry
            // is a criterion with ≥1 judgment finding. A flagged criterion
            // with no findings at all keeps its fallback row — failed when not
            // in the judgment state, needs-your-judgment otherwise.
            const modalFailureEntries = failedEntries.filter(([k, v]) =>
              v === 1 &&
              (((failedIssuesByCriterion[k]?.length ?? 0) > 0) ||
                (!judgmentKeys.has(k) && !((judgmentIssuesByCriterion[k]?.length ?? 0) > 0))),
            );
            const modalJudgmentEntries = failedEntries.filter(([k, v]) =>
              v === 1 &&
              (((judgmentIssuesByCriterion[k]?.length ?? 0) > 0) ||
                (judgmentKeys.has(k) && !((failedIssuesByCriterion[k]?.length ?? 0) > 0))),
            );
            const judgmentCount = modalJudgmentEntries.length;
            const summaryFailedCount = modalFailureEntries.length;
            const tone =
              evaluatedCount === 0 ? "not-evaluated" :
              failedCount === 0 ? "pass" :
              failedCount <= 3 ? "warn" : "fail";
            return (
              <div
                className={cn(
                  "rounded-md border p-3 space-y-2",
                  tone === "pass" && "border-green-300 bg-green-50",
                  tone === "warn" && "border-orange-300 bg-orange-50",
                  tone === "fail" && "border-red-300 bg-red-50",
                )}
              >
                <div className="flex items-center gap-2">
                  {/* ARC-359: shield and "Item Quality:" read dark; the counts
                      keep the tone colours. */}
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-semibold text-foreground">
                    <ItemQualitySummary
                      passed={passedCount}
                      failed={summaryFailedCount}
                      notEvaluated={notEvaluated}
                      judgment={judgmentCount}
                    />
                  </span>
                  {evaluatedCount > 0 && failedCount === 0 && (
                    <Badge variant="outline" className="text-green-600 border-green-300 bg-white">
                      PASS
                    </Badge>
                  )}
                </div>
                {(() => {
                  // "Don't Teach" is a judgment call, not a defect, so the
                  // tailored guidance below (not the raw red list) is what
                  // carries it: its raw "Z: ..." messages come out of the red
                  // issue list and are replaced by the guidance for whichever
                  // half fired. The advisory array comes from the DISPLAYED
                  // cycle first (live payload shape), falling back to the
                  // question-level field (legacy rows / dev sample) — same
                  // pairing rule as the card, so the modal can't drift from
                  // it.
                  const dontTeach = dontTeachGuidance(flags, rawIssues, letterMap, scoreFeedback);
                  // Don't Teach messages are handled by the blue judgment
                  // group below, never by the raw red list — whether or not
                  // this cycle flags the criterion (ARC-219 / ARC-240).
                  // Findings were bucketed above (bucketSocratesFindings):
                  // advisory → blue Needs your judgment, every other finding →
                  // red Open issues, each finding split into its own bullet.
                  // This block only renders the two headings from those
                  // shared buckets.
                  if (
                    modalFailureEntries.length === 0 &&
                    unattributedFeedback.length === 0 &&
                    modalJudgmentEntries.length === 0
                  ) return null;
                  return (
                    <div className="space-y-3">
                      {modalJudgmentEntries.length > 0 && (
                        <div data-testid="socrates-judgment-group-modal">
                          <p className="text-sm font-semibold text-foreground pl-6">
                            Needs your judgment
                          </p>
                          {dontTeach.flagged && (
                            <div className="pl-6">
                              <div
                                className="space-y-0.5"
                                data-testid="socrates-judgment-dont-teach-modal"
                              >
                                <p className="text-xs font-semibold text-blue-600">
                                  {dontTeach.heading}
                                </p>
                                {dontTeach.showStem && (
                                  <p className="text-xs text-blue-600">
                                    {DONT_TEACH_STEM_GUIDANCE}
                                  </p>
                                )}
                                {dontTeach.showOptions &&
                                  (dontTeach.optionGroups.length > 0 ? (
                                    dontTeach.optionGroups.map((group, i) => (
                                      <p key={i} className="text-xs text-blue-600">
                                        {dontTeachOptionsMessage(group)}
                                      </p>
                                    ))
                                  ) : (
                                    <p className="text-xs text-blue-600">
                                      {DONT_TEACH_OPTIONS_GUIDANCE}
                                    </p>
                                  ))}
                                {dontTeachAdvisorySuggestion(scoreFeedback) && (
                                  <p
                                    className="text-xs text-blue-600 italic mt-1"
                                    data-testid="criterion-suggestion-do_not_teach_in_stem"
                                  >
                                    {dontTeachAdvisorySuggestion(scoreFeedback)}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {modalJudgmentEntries.filter(([key]) => key !== DONT_TEACH_KEY).map(([key]) => {
                            const items = judgmentIssuesByCriterion[key] ?? [];
                            return (
                              <div key={key} className="pl-6">
                                <div
                                  className="space-y-0.5"
                                  data-testid={`socrates-judgment-row-${key}`}
                                >
                                  <p className="text-xs font-semibold text-blue-600">
                                    {CRITERIA_LABELS[key] || key}
                                  </p>
                                  {items.length > 0 ? (
                                    items.map((d, i) => (
                                      <p key={i} className="text-xs text-blue-600">• {d}</p>
                                    ))
                                  ) : (
                                    <p className="text-xs text-blue-600 italic">
                                      Flagged, no details available.
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(modalFailureEntries.length > 0 || unattributedFeedback.length > 0) && (
                        <>
                          <p className="text-sm font-semibold text-foreground pl-6">
                            Open issues
                          </p>
                          {modalFailureEntries.map(([key]) => {
                            const items = failedIssuesByCriterion[key] ?? [];
                            return (
                              <div key={key} className="pl-6">
                                <p className="text-xs font-semibold text-red-700">
                                  {CRITERIA_LABELS[key] || key}
                                </p>
                                {items.length > 0 ? (
                                  items.map((d, i) => (
                                    <p key={i} className="text-xs text-red-600">
                                      • {d}
                                    </p>
                                  ))
                                ) : (
                                  <p className="text-xs text-red-600 italic">
                                    Flagged, no details available.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                          {unattributedFeedback.length > 0 && (
                            <div className="space-y-1" data-testid="unattributed-feedback">
                              <p className="text-sm font-semibold text-foreground pl-6">
                                Unattributed
                              </p>
                              {unattributedFeedback.map((f, i) => (
                                <div key={i} className="pl-6">
                                  <div className="space-y-0.5">
                                    <p className="text-xs text-muted-foreground">{f.description}</p>
                                    {f.suggestion.length > 0 && (
                                      <p className="text-xs text-muted-foreground italic">
                                        {f.suggestion}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
                {/* ARC-206: not-evaluated criteria get their own section
                    directly beneath the issues list (which stays a list of
                    defects to act on) — same presentation as the item card. */}
                {notEvaluatedEntries.length > 0 && (
                  <div className="space-y-1" data-testid="not-evaluated-section">
                    <p className="text-sm font-semibold text-foreground pl-6">Not evaluated</p>
                    {notEvaluatedEntries.map(([key]) => (
                      <div key={key} className="pl-6">
                        <p className="text-xs font-semibold text-muted-foreground">
                          {CRITERIA_LABELS[key] || key}
                        </p>
                        <p className="text-xs text-muted-foreground italic">
                          {notEvaluatedReason(lastCycle, key)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {scoreError && (
            <p className="text-sm text-destructive">{scoreError}</p>
          )}

          {/* Inline hint explaining why Update is disabled — only surfaced
              once the user actually tries to Update without scoring (same
              trigger as the red ring on Score). Branches on whether they've
              seen a score yet: "re-score after edits" vs "score first time." */}
          {highlightScore && (
            <p className="text-xs font-semibold text-foreground text-right -mb-2">
              {scoreData
                ? "Re-score your changes to enable Update."
                : "Score this question to enable Update."}
            </p>
          )}
        </div>

        {/* Cancel pinned bottom-left; the Score/Update action pair stays
            bottom-right where users expect the primary actions. */}
        <DialogFooter className="pt-2 sm:justify-between">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleScore}
              disabled={scoringState === "scoring" || generatingAt !== null}
              className={cn(highlightScore && "ring-2 ring-red-500 ring-offset-1")}
            >
              {scoringState === "scoring" ? (
                <>Scoring… <Loader2 className="h-4 w-4 animate-spin" /></>
              ) : "Score"}
            </Button>
            {/* Update stays clickable while unscored (so we can catch the click
                and point the user at Score) but looks disabled. It's only truly
                disabled mid-generation, when scoring isn't possible anyway. */}
            <Button
              onClick={() => {
                if (scoringState !== "complete") {
                  setHighlightScore(true);
                  return;
                }
                handleSave();
              }}
              disabled={generatingAt !== null}
              aria-disabled={scoringState !== "complete"}
              className={cn(
                scoringState !== "complete" &&
                  "opacity-50 cursor-pointer hover:bg-primary",
              )}
            >
              Update
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
